import React, { useEffect, useState } from "react";
import {
    ArrowRightIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import { useSystemLogo } from "../../context/systemConfigContext";

export default function DatosContactoPage() {
    const RESERVE_DRAFT_KEY = "reserve_draft_v1";
    const navigate = useNavigate();
    const location = useLocation();
    const { logo } = useSystemLogo();
    const [cities, setCities] = useState([]);
    const [cityId, setCityId] = useState("");
    const [cityQuery, setCityQuery] = useState("");
    const [cityOpen, setCityOpen] = useState(false);
    const [phone, setPhone] = useState("");
    const [birthDate, setBirthDate] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const readDraft = () => {
        try {
            const raw = sessionStorage.getItem(RESERVE_DRAFT_KEY);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    };

    const writeDraft = (patch) => {
        try {
            const current = readDraft() ?? {};
            const next = { ...current, ...patch };
            sessionStorage.setItem(RESERVE_DRAFT_KEY, JSON.stringify(next));
        } catch {
            // ignore
        }
    };

    const persistContactDraft = (data) => {
        writeDraft({
            contact: {
                cityId: (data?.cityId ?? "").toString(),
                cityQuery: (data?.cityQuery ?? "").toString(),
                phone: (data?.phone ?? "").toString(),
                birthDate: (data?.birthDate ?? "").toString(),
                email: (data?.email ?? "").toString(),
            },
        });
    };

    const updateContactDraft = (patch) => {
        const current = readDraft()?.contact ?? {};
        persistContactDraft({
            cityId,
            cityQuery,
            phone,
            birthDate,
            email,
            ...current,
            ...patch,
        });
    };

    useEffect(() => {
        let mounted = true;
        api.cities
            .getMulti()
            .then((data) => {
                if (!mounted) return;
                setCities(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!mounted) return;
                setCities([]);
            });
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        const c = readDraft()?.contact ?? null;
        if (c && typeof c === "object") {
            setCityId(typeof c.cityId === "string" ? c.cityId : "");
            setCityQuery(typeof c.cityQuery === "string" ? c.cityQuery : "");
            setPhone(typeof c.phone === "string" ? c.phone : "");
            setBirthDate(typeof c.birthDate === "string" ? c.birthDate : "");
            setEmail(typeof c.email === "string" ? c.email : "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!cityId) return;
        const selected = cities.find((c) => String(c?.id) === String(cityId));
        if (selected?.name) setCityQuery(selected.name);
    }, [cities, cityId]);

    const splitFullName = (value) => {
        const cleaned = (value ?? "").trim().replace(/\s+/g, " ");
        if (!cleaned) return { firstName: "", lastName: "" };
        const parts = cleaned.split(" ");
        if (parts.length === 1) return { firstName: parts[0], lastName: "" };
        return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
    };

    const parseBirthdate = (value) => {
        const v = (value ?? "").trim();
        if (!v) return null;
        const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return null;
        const [, dd, mm, yyyy] = m;
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatBirthDateInput = (value) => {
        const raw = (value ?? "").replace(/[^\d/]/g, "");

        const hasSlash = raw.includes("/");
        if (hasSlash) {
            const rawParts = raw.split("/");

            const dd = (rawParts[0] ?? "").replace(/\D/g, "").slice(0, 2);
            const mmAndMaybeYear = (rawParts[1] ?? "").replace(/\D/g, "");
            const mm = mmAndMaybeYear.slice(0, 2);
            const yearFromOverflow = mmAndMaybeYear.slice(2, 6);
            const yyyyFromThird = (rawParts[2] ?? "").replace(/\D/g, "").slice(0, 4);
            const yyyy = (yyyyFromThird || yearFromOverflow).slice(0, 4);

            const hasFirstSlash = rawParts.length > 1;
            const hasSecondSlash = rawParts.length > 2;
            const shouldAutoInsertSecondSlash =
                !hasSecondSlash && mmAndMaybeYear.length > 2 && yyyy.length > 0;

            let out = dd;
            if (hasFirstSlash && (raw.endsWith("/") || mm.length > 0)) {
                out += "/";
            }
            if (mm.length > 0) out += mm;

            if ((hasSecondSlash && mm.length > 0 && (raw.endsWith("/") || yyyy.length > 0)) || shouldAutoInsertSecondSlash) {
                out += "/";
            }

            if (yyyy.length > 0) out += yyyy;
            return out.slice(0, 10);
        }

        const digits = raw.replace(/\D/g, "").slice(0, 8);
        const dd = digits.slice(0, 2);
        const mm = digits.slice(2, 4);
        const yyyy = digits.slice(4, 8);
        if (digits.length <= 2) return dd;
        if (digits.length <= 4) return `${dd}/${mm}`;
        return `${dd}/${mm}/${yyyy}`;
    };

    const filteredCities = cities
        .filter((c) => {
            const q = cityQuery.trim().toLowerCase();
            if (!q) return true;
            return (c?.name ?? "").toLowerCase().includes(q);
        })
        .slice(0, 500);

    const onSelectCity = (c) => {
        const nextId = String(c?.id ?? "");
        const nextName = c?.name ?? "";
        setCityId(nextId);
        setCityQuery(nextName);
        updateContactDraft({ cityId: nextId, cityQuery: nextName });
        setCityOpen(false);
    };

    const onRegister = async () => {
        setErrorMessage("");
        const draft = readDraft();
        const govId = (
            location.state?.govId ?? draft?.personal?.govId ?? ""
        )
            .toString()
            .trim();
        const fullName = (
            location.state?.fullName ?? draft?.personal?.fullName ?? ""
        )
            .toString()
            .trim();
        const sex = (
            location.state?.sex ?? draft?.personal?.sex ?? ""
        ).toString();
        const phoneTrimmed = phone.trim();
        const parsedCityId = cityId ? Number(cityId) : null;

        if (!govId || !fullName || !sex || !phoneTrimmed || !parsedCityId) {
            setErrorMessage("Completá los campos obligatorios.");
            return;
        }

        const { firstName, lastName } = splitFullName(fullName);
        const birthdate = parseBirthdate(birthDate);

        const payload = {
            govId,
            firstName,
            lastName,
            legalName: `${firstName} ${lastName}`.trim(),
            ruc: govId,
            sex: Number(sex),
            email: email.trim() ? email.trim() : null,
            birthdate,
            city: parsedCityId,
            confirmedData: true,
            registrationDate: new Date().toISOString().split("T")[0],
            phoneNumbers: [{ number: phoneTrimmed }],
            dm: false,
            hta: false,
            aler: false,
            gl: false,
            strabismus: false,
            glaucoma: false,
            cataractSurgery: false,
            glaucomaSurgery: false,
            refractiveSurgery: false,
            motherDm: false,
            motherHta: false,
            motherGlaucoma: false,
            fatherDm: false,
            fatherHta: false,
            fatherGlaucoma: false,
            siblingsDm: false,
            siblingsHta: false,
            siblingsGlaucoma: false,
            otherDm: false,
            otherHta: false,
            otherGlaucoma: false,
        };

        try {
            setLoading(true);
            const patient = await api.patients.create(payload);
            const patientName = `${patient?.firstName ?? firstName} ${
                patient?.lastName ?? lastName
            }`.trim();
            navigate("/reserve/select-doctor", {
                state: {
                    patient,
                    patientName: patientName || fullName,
                    govId,
                },
            });
        } catch (e) {
            const formatApiError = (err) => {
                const msg = err?.message;
                if (typeof msg === "string") return msg;
                if (msg && typeof msg === "object") {
                    const lines = [];
                    Object.entries(msg).forEach(([key, val]) => {
                        if (Array.isArray(val)) {
                            val.forEach((v) => {
                                if (typeof v === "string" && v.trim()) {
                                    lines.push(`${key}: ${v}`);
                                }
                            });
                        } else if (typeof val === "string" && val.trim()) {
                            lines.push(`${key}: ${val}`);
                        }
                    });
                    return lines.length
                        ? lines.join("\n")
                        : "No pudimos registrar al paciente. Intentá nuevamente.";
                }
                return "No pudimos registrar al paciente. Intentá nuevamente.";
            };

            setErrorMessage(
                formatApiError(e)
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F1F1FE] px-4 py-6 font-poppins md:flex md:items-center md:justify-center">
            <div className="w-full max-w-sm mx-auto md:max-w-[520px]">
                <div className="hidden md:block">
                    <div className="relative flex items-center justify-center">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="absolute left-0 flex items-center gap-2 text-[#000000] font-medium"
                        >
                            <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                                <ChevronLeftIcon className="h-4 w-4 text-white" />
                            </div>
                            <span>Atrás</span>
                        </button>
                        <img className="h-auto w-40" src={logo} alt="Logo" />
                    </div>
                </div>

                <div className="md:hidden">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-[#000000] font-medium"
                    >
                        <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                            <ChevronLeftIcon className="h-4 w-4 text-white" />
                        </div>
                        <span>Atrás</span>
                    </button>
                </div>

                <div className="mt-5 rounded-2xl bg-[#E3E2FF] px-4 py-4 flex gap-3">
                    <InformationCircleIcon className="h-6 w-6 text-primary flex-shrink-0" />
                    <div>
                        <p className="text-sm font-semibold text-primary tracking-[0.3px]">
                            No encontramos su número en el sistema.
                        </p>
                        <p className="mt-1 text-xs text-[#000000] leading-4 tracking-[0.3px]">
                            Por favor, complete sus datos para que
                            podamos atenderlo.
                        </p>
                    </div>
                </div>

                <div className="mt-6 bg-white rounded-2xl shadow-sm px-6 py-7">
                    <h1 className="text-center text-[24px] font-semibold text-primary tracking-[0.5px]">
                        Datos de Contacto
                    </h1>
                    <p className="mt-2 text-center text-sm text-[#000000] tracking-[0.3px]">
                        Paso 2 de 2
                    </p>

                    <div className="mt-4 flex justify-center">
                        <div className="flex items-center gap-3">
                            <div className="h-[4px] w-20 rounded-full bg-primary" />
                            <div className="h-[4px] w-20 rounded-full bg-primary" />
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Ciudad *
                        </label>
                        <div className="relative mt-2">
                            <input
                                type="text"
                                value={cityQuery}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setCityQuery(v);
                                    setCityId("");
                                    updateContactDraft({ cityQuery: v, cityId: "" });
                                    setCityOpen(true);
                                }}
                                onFocus={() => setCityOpen(true)}
                                onBlur={() => setTimeout(() => setCityOpen(false), 120)}
                                placeholder="Escribí tu ciudad"
                                className="input-base px-3 py-3 text-sm"
                                autoComplete="off"
                            />
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setCityOpen((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                            </button>

                            {cityOpen ? (
                                <div className="absolute z-10 mt-2 w-full rounded-xl border border-[#ECECFF] bg-white shadow-sm max-h-60 overflow-auto">
                                    {filteredCities.length ? (
                                        filteredCities.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => onSelectCity(c)}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-[#F7F7FF]"
                                            >
                                                {c.name}
                                            </button>
                                        ))
                                    ) : (
                                        <div className="px-3 py-2 text-sm text-[#667085]">
                                            Sin resultados
                                        </div>
                                    )}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Celular / Teléfono *
                        </label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => {
                                const v = e.target.value;
                                setPhone(v);
                                updateContactDraft({ phone: v });
                            }}
                            placeholder="Ej: 098 123 456"
                            className="mt-2 input-base px-3 py-3 text-sm"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Fecha de Nacimiento *
                        </label>
                        <input
                            type="text"
                            value={birthDate}
                            onChange={(e) =>
                                (() => {
                                    const v = formatBirthDateInput(e.target.value);
                                    setBirthDate(v);
                                    updateContactDraft({ birthDate: v });
                                })()
                            }
                            placeholder="dd/mm/aaaa"
                            inputMode="numeric"
                            maxLength={10}
                            className="mt-2 input-base px-3 py-3 text-sm"
                        />
                    </div>

                    <div className="mt-4">
                        <label className="block text-xs font-semibold text-[#000000]">
                            Correo electrónico
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                                const v = e.target.value;
                                setEmail(v);
                                updateContactDraft({ email: v });
                            }}
                            placeholder="Ej: correo@ejemplo.com"
                            className="mt-2 input-base px-3 py-3 text-sm"
                        />
                    </div>

                    <button
                        type="button"
                        onClick={onRegister}
                        disabled={loading}
                        className="mt-6 w-full rounded-lg bg-primary text-white font-medium py-3 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-b-2 border-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <span>Registrarme</span>
                                <ArrowRightIcon className="h-5 w-5" />
                            </>
                        )}
                    </button>

                    {errorMessage ? (
                        <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
                            {errorMessage}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
